import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    timeout: 180000,
    withCredentials: true, // required for Emergent Google Auth cookie-based session
});

export const getContext = (window = 3) =>
    api.get(`/fpl/context`, { params: { window } }).then((r) => r.data);

export const generateChallenges = (payload) =>
    api.post(`/generate-challenges`, payload).then((r) => r.data);

export const listSkills = () => api.get(`/skills`).then((r) => r.data);

export const downloadXlsxUrl = (id) =>
    `${API}/challenges/${id}/export.xlsx`;

export const downloadBulkXlsxUrl = (status) =>
    status ? `${API}/challenges/export.xlsx?status=${status}` : `${API}/challenges/export.xlsx`;

export const listChallenges = (status) =>
    api.get(`/challenges`, { params: status ? { status } : {} }).then((r) => r.data);

export const getChallenge = (id) => api.get(`/challenges/${id}`).then((r) => r.data);

export const updateChallenge = (id, patch) =>
    api.patch(`/challenges/${id}`, patch).then((r) => r.data);

export const deleteChallenge = (id) => api.delete(`/challenges/${id}`).then((r) => r.data);

export const publishChallenge = (id) =>
    api.post(`/challenges/${id}/publish`).then((r) => r.data);

export const validateChallenge = (id) =>
    api.post(`/challenges/${id}/validate`).then((r) => r.data);

// ---- Prediction conviction votes -----------------------------------------

export const castPredictionVote = (playerId, payload) =>
    api.post(`/predictions/${playerId}/vote`, payload).then((r) => r.data);

export const getPredictionVotes = (playerId, gw_window = 3) =>
    api.get(`/predictions/${playerId}/votes`, { params: { gw_window } }).then((r) => r.data);

export const fetchVotesBulk = (player_ids, gw_window = 3) =>
    api.post(`/predictions/votes/bulk`, { player_ids, gw_window }).then((r) => r.data);

// ---- News signals --------------------------------------------------------

export const listNewsSignals = (params = {}) =>
    api.get(`/news-signals`, { params }).then((r) => r.data);

export const createNewsSignal = (payload) =>
    api.post(`/news-signals`, payload).then((r) => r.data);

export const deleteNewsSignal = (id) =>
    api.delete(`/news-signals/${id}`).then((r) => r.data);

// ---- User profile --------------------------------------------------------

export const getUserProfile = (userId) =>
    api.get(`/users/${userId}/profile`).then((r) => r.data);

// ---- Market advice + outlook --------------------------------------------

export const castMarketAdvice = (playerId, advice) =>
    api.post(`/market/${playerId}/advice`, { advice }).then((r) => r.data);

export const getMarketOutlook = (playerId) =>
    api.get(`/market/${playerId}/outlook`).then((r) => r.data);

// ---- Vote resolution (admin) --------------------------------------------

export const resolveVotes = (upToGw) =>
    api.post(`/predictions/votes/resolve`, null, { params: { up_to_gw: upToGw } }).then((r) => r.data);

export const markReadyForReview = (id) =>
    api.post(`/challenges/${id}/ready-for-review`).then((r) => r.data);

export const archiveChallenge = (id) =>
    api.post(`/challenges/${id}/archive`).then((r) => r.data);

export const generateImage = (id) =>
    api.post(`/challenges/${id}/image`).then((r) => r.data);

export const listGenerations = () => api.get(`/generations`).then((r) => r.data);

export const getGeneration = (id) => api.get(`/generations/${id}`).then((r) => r.data);

export const getThreatDashboard = (window = 3) =>
    api.get(`/threat-dashboard`, { params: { window } }).then((r) => r.data);

export const getMarket = (limit = 50) =>
    api.get(`/market`, { params: { limit } }).then((r) => r.data);

// ---- Leaderboard + Notifications ---------------------------------------

export const getCurrentGwStatus = () =>
    api.get(`/season/current-gw`).then((r) => r.data);

// ---- Market Decision (Transfer Desk) ----------------------------------

export const generateMarketDecisionChallenge = (payload) =>
    api.post(`/challenges/generate-market-decision`, payload).then((r) => r.data);

export const submitMarketDecisions = (cid, decisions, opts = {}) => {
    const body = { decisions };
    if (opts.confidences) body.confidences = opts.confidences;
    if (opts.displayName) body.display_name = opts.displayName;
    if (opts.email) body.email = opts.email;
    return api.post(`/challenges/${cid}/decisions`, body).then((r) => r.data);
};

export const getMyMarketDecisions = (cid, guestUserId = null) => {
    const params = guestUserId ? { user_id: guestUserId } : {};
    return api.get(`/challenges/${cid}/my-decisions`, { params }).then((r) => r.data);
};

export const getChallengeLeaderboard = (cid, limit = 20) =>
    api.get(`/challenges/${cid}/leaderboard`, { params: { limit } }).then((r) => r.data);

export const getCommunityDecisions = (cid) =>
    api.get(`/challenges/${cid}/community-decisions`).then((r) => r.data);

export const getChallengeSummary = (cid) =>
    api.get(`/challenges/${cid}/summary`).then((r) => r.data);

export const getUserArchetype = (userId) =>
    api.get(`/users/${userId}/archetype`).then((r) => r.data);

export const buildShareCardUrl = (cid, userId) =>
    `${API}/challenges/${cid}/share-card/${userId}`;

export const getActiveWeeklyChallenge = () =>
    api.get(`/challenges/active-weekly`).then((r) => r.data);

export const submitGuestMarketDecisions = (cid, decisions, displayName, email) =>
    api.post(`/challenges/${cid}/decisions`, {
        decisions,
        display_name: displayName,
        email: email || null,
    }).then((r) => r.data);

export const getAdvisorLeaderboard = (limit = 20) =>
    api.get(`/leaderboard/advisors`, { params: { limit } }).then((r) => r.data);

export const getNotifications = (unreadOnly = false, limit = 30) =>
    api.get(`/notifications`, { params: { unread_only: unreadOnly, limit } }).then((r) => r.data);

export const markAllNotificationsRead = () =>
    api.post(`/notifications/read-all`).then((r) => r.data);

// ---- Provider mapping (Phase C) ----
export const rebuildMappings = (useNutmeg = true) =>
    api.post(`/mapping/rebuild`, null, { params: { use_nutmeg: useNutmeg } }).then((r) => r.data);

export const listMappingTeams = () =>
    api.get(`/mapping/teams`).then((r) => r.data);

export const listMappingPlayers = ({ confidence, q, teamId, limit = 200 } = {}) =>
    api
        .get(`/mapping/players`, {
            params: {
                ...(confidence && confidence.length ? { confidence: confidence.join(",") } : {}),
                ...(q ? { q } : {}),
                ...(teamId ? { team_id: teamId } : {}),
                limit,
            },
        })
        .then((r) => r.data);

export const verifyPlayerMapping = (fplId, payload) =>
    api.post(`/mapping/players/${fplId}/verify`, payload).then((r) => r.data);

export const getSportmonksStatus = () =>
    api.get(`/sportmonks/status`).then((r) => r.data);

// ---- Squad Planner v2 (P2) ----
export const getManagerSquad = ({ managerId, gameweek, window = 5, targets } = {}) =>
    api
        .get(`/manager/${managerId}/squad`, {
            params: {
                ...(gameweek ? { gameweek } : {}),
                window,
                ...(targets ? { targets } : {}),
            },
            timeout: 90000,
        })
        .then((r) => r.data);

export const getTopManagers = (pageCount = 1) =>
    api.get(`/manager/top`, { params: { page_count: pageCount } }).then((r) => r.data);

export const whatIfSwap = (managerId, payload) =>
    api.post(`/manager/${managerId}/whatif`, payload, { timeout: 120000 }).then((r) => r.data);

export const createPlan = (payload) =>
    api.post(`/plans`, payload).then((r) => r.data);

export const listManagerPlans = (managerId) =>
    api.get(`/manager/${managerId}/plans`).then((r) => r.data);

export const getPlan = (planId) => api.get(`/plans/${planId}`).then((r) => r.data);

export const updatePlan = (planId, patch) =>
    api.patch(`/plans/${planId}`, patch).then((r) => r.data);

export const deletePlan = (planId) =>
    api.delete(`/plans/${planId}`).then((r) => r.data);

export const simulateTransferPlan = (managerId, payload) =>
    api.post(`/manager/${managerId}/transfer-plan`, payload, { timeout: 180000 }).then((r) => r.data);

export const getTopPredictions = (window = 5, topN = 200) =>
    api.get(`/predictions`, { params: { window, top_n: topN } }).then((r) => r.data);

export const getPredictionsV2 = (params = {}) =>
    api.get(`/predictions/v2`, { params, timeout: 60000 }).then((r) => r.data);
export const getCaptainPlan = (params = {}) =>
    api.get(`/predictions/v2/captain`, { params, timeout: 90000 }).then((r) => r.data);
export const getPredictionAiSummary = (window = 1, refresh = false) =>
    api.get(`/predictions/v2/ai-summary`, { params: { window, refresh }, timeout: 120000 }).then((r) => r.data);
export const getMarketWatch = () =>
    api.get(`/market/watch`, { timeout: 60000 }).then((r) => r.data);
export const getPredictionAccuracy = () =>
    api.get(`/predictions/v2/accuracy`).then((r) => r.data);

// ---- Community challenge submissions ----
export const submitCommunityChallenge = (payload) =>
    api.post(`/challenges/community-submit`, payload).then((r) => r.data);

// Returns the absolute URL for the bulk ZIP export endpoint (used with fetch POST).
export const bulkExportZipUrl = () => `${API}/challenges/bulk-export`;

export const approveCommunityChallenge = (cid) =>
    api.post(`/challenges/${cid}/approve`).then((r) => r.data);

export const rejectCommunityChallenge = (cid, reason) =>
    api
        .post(`/challenges/${cid}/reject`, null, { params: reason ? { reason } : {} })
        .then((r) => r.data);

// ---- P1 backlog (player detail + FDR heatmap + async jobs) ----
export const getPlayerDetail = (playerId) =>
    api.get(`/players/${playerId}/detail`).then((r) => r.data);

export const getFdrHeatmap = (window = 6, startGw, mode = "gw", view = "official") =>
    api
        .get(`/fdr/heatmap`, { params: { window, mode, view, ...(startGw ? { start_gw: startGw } : {}) } })
        .then((r) => r.data);

export const startAsyncGeneration = (payload) =>
    api.post(`/generate-challenges/async`, payload).then((r) => r.data);

export const getJobStatus = (jobId) =>
    api.get(`/jobs/${jobId}`).then((r) => r.data);


// ---- Qualification (multi-tier race) ----
export const getQualification = () =>
    api.get(`/qualification`).then((r) => r.data);


// ---- Auth (Emergent Google) ----
export const exchangeAuthSession = (sessionId) =>
    api.post(`/auth/session`, { session_id: sessionId }).then((r) => r.data);

export const getAuthMe = () => api.get(`/auth/me`).then((r) => r.data);

export const authLogout = () => api.post(`/auth/logout`).then((r) => r.data);

export const getPublicUser = (userId) =>
    api.get(`/users/${userId}`).then((r) => r.data);

// ---- Community v2: comments + voting ----
export const listComments = (cid) =>
    api.get(`/challenges/${cid}/comments`).then((r) => r.data);

export const createComment = (cid, body) =>
    api.post(`/challenges/${cid}/comments`, { body }).then((r) => r.data);

export const deleteComment = (cid, commentId) =>
    api.delete(`/challenges/${cid}/comments/${commentId}`).then((r) => r.data);

export const upvoteComment = (cid, commentId) =>
    api.post(`/challenges/${cid}/comments/${commentId}/upvote`).then((r) => r.data);

export const getChallengeVote = (cid) =>
    api.get(`/challenges/${cid}/vote`).then((r) => r.data);

export const castChallengeVote = (cid, value) =>
    api.post(`/challenges/${cid}/vote`, { value }).then((r) => r.data);


// ---- Europe slots overrides (FA Cup winners, EL finalists, etc.) -----------
export const getEuropeSlots = (season) =>
    api.get(`/europe-slots`, { params: season ? { season } : {} }).then((r) => r.data);

export const saveEuropeSlots = (payload, season) =>
    api.put(`/europe-slots`, payload, { params: season ? { season } : {} }).then((r) => r.data);


// ---- World Cup 2026 ---------------------------------------------------------
export const getWcOverview = () =>
    api.get(`/worldcup/overview`).then((r) => r.data);

export const getWcGroups = () =>
    api.get(`/worldcup/groups`).then((r) => r.data);

export const getWcTeams = () =>
    api.get(`/worldcup/teams`).then((r) => r.data);

export const getWcTeam = (id) =>
    api.get(`/worldcup/teams/${id}`).then((r) => r.data);

export const getWcMatches = (params = {}) =>
    api.get(`/worldcup/matches`, { params }).then((r) => r.data);

export const getWcPlayers = (params = {}) =>
    api.get(`/worldcup/players`, { params }).then((r) => r.data);

export const getMatchdayLeaderboard = (md, limit = 50) =>
    api.get(`/worldcup/matchday-call/${md}/leaderboard`, { params: { limit } }).then((r) => r.data);

export const syncWorldCup = () =>
    api.post(`/worldcup/sync`).then((r) => r.data);

export const getMatchdayCall = (md) =>
    api.get(`/worldcup/matchday-call/${md}`).then((r) => r.data);

export const generateMatchdayCall = (md, opts = {}) =>
    api.post(`/worldcup/matchday-call/generate`, null, {
        params: {
            matchday: md,
            picks_per_team: opts.picks_per_team ?? 5,
            regenerate: opts.regenerate ?? false,
        },
    }).then((r) => r.data);

export const resolveMatchdayCall = (md, requireAllFinished = true) =>
    api.post(`/worldcup/matchday-call/${md}/resolve`, null, { params: { require_all_finished: requireAllFinished } }).then((r) => r.data);

export const verifyEuropeSlots = (season) =>
    api.post(`/europe-slots/verify`, null, { params: season ? { season } : {} }).then((r) => r.data);
